import { Box } from "@mui/material"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import React from "react"
import { twMerge } from "tailwind-merge"

import StyledChip from "@/components/common/Chip/Chip"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { ProductStatusCell } from "@/components/table"
import type { Product } from "@/graphql/codegen/graphql"
import { CompanyStatus } from "@/graphql/codegen/graphql"
import ProductRowActions from "@/screens/Products/components/tables/productRowActions"
import { productStockTypeLabelMap } from "@/screens/Products/utils/productsTableConfig"
import { createStatusMap } from "@/utils/statusMap"

const companyStatusMap = createStatusMap<CompanyStatus, "secondary" | "error" | "success" | "warning">([
  [CompanyStatus.Active, "success"],
  [CompanyStatus.Pending, "warning"],
  [CompanyStatus.Rejected, "error"],
  [CompanyStatus.Inactive, "secondary"],
])

interface UseProductListColumnsProps {
  onToggleProductStatus: () => void
}

export const useProductListColumns = ({ onToggleProductStatus }: UseProductListColumnsProps): ColumnDef<Product>[] => {
  const handleRenderCompanyName = ({ row }: CellContext<Product, unknown>) => {
    const product = row.original
    return (
      <Box className='ml-3 flex items-center justify-start gap-2 px-1'>
        <StatusIndicator status={product?.company?.status ?? CompanyStatus.Inactive} map={companyStatusMap} />
        <div>{product?.company?.name}</div>
      </Box>
    )
  }

  return [
    {
      accessorKey: "publicId",
      header: "Product ID",
      sortingFn: "alphanumeric",
      cell: ({ row }) => <ProductStatusCell product={row.original} />,
      enableSorting: true,
    },
    {
      accessorKey: "companyProductDescription",
      header: "Akrochem Description",
      cell: ({ row }) => <Box className='ml-3.5'>{row.original.companyProductDescription} </Box>,
      sortingFn: "alphanumeric",
      enableSorting: true,
    },
    {
      accessorKey: "companyName",
      header: "Company Name",
      accessorFn: (row) => row.company?.name,
      cell: handleRenderCompanyName,
      sortingFn: "alphanumeric",
      enableSorting: true,
    },
    {
      accessorKey: "vendorDescription",
      header: "Supplier Description",
      cell: ({ row }) => <Box className='ml-4'>{row.original.vendorDescription} </Box>,
      sortingFn: "alphanumeric",
      enableSorting: true,
    },
    {
      accessorKey: "stockType",
      header: "Stocked In",
      cell: ({ row }: CellContext<Product, unknown>) => {
        const stockType = row.original.stockType
        return (
          <Box>
            {stockType && (
              <StyledChip
                size='small'
                className={twMerge("rounded-s border-none text-xs text-gray-800")}
                classes={{
                  colorDefault: "bg-tertiary-yellow-300",
                }}
                variant='outlined'
                label={productStockTypeLabelMap.get(stockType)}
              />
            )}
          </Box>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }) => <ProductRowActions onToggleProductStatus={onToggleProductStatus} row={row} />,
      size: 40,
      enableSorting: false,
    },
  ]
}
